// "Reachable within" slider. Positions run from band_range.min to band_range.max in `step`
// increments, plus one position past the end meaning "Any" (no filter, state value 0).
// Built once and updated in place so dragging is never interrupted by a re-render.

import { formatBandLabel, snapToStep } from "../logic";

export interface ReachSliderConfig {
  min: number;
  max: number;
  step: number;
  /** Values that get a printed tick label under the track, e.g. [60, 120, 180, 240]. */
  bands: number[];
  onChange: (minutes: number) => void;
}

export interface ReachSlider {
  /** Reflect a state value (0 = Any) without firing onChange. */
  update(minutes: number): void;
}

export function createReachSlider(
  container: HTMLElement,
  output: HTMLOutputElement,
  cfg: ReachSliderConfig,
): ReachSlider {
  const anyValue = cfg.max + cfg.step; // the extra position past the end
  container.textContent = "";

  const input = document.createElement("input");
  input.type = "range";
  input.id = "reach-input";
  input.min = String(cfg.min);
  input.max = String(anyValue);
  input.step = String(cfg.step);
  input.className = "reach-input";
  input.setAttribute("aria-labelledby", "reach-label");
  output.htmlFor = "reach-input";

  const scale = document.createElement("div");
  scale.className = "reach-scale";
  scale.setAttribute("aria-hidden", "true");
  const ticks = [...cfg.bands.filter((b) => b >= cfg.min && b < cfg.max), anyValue];
  for (const t of ticks) {
    const span = document.createElement("span");
    span.textContent = t === anyValue ? "Any" : formatBandLabel(t);
    // Position each label at its fraction of the track so the ticks line up with the thumb.
    span.style.left = `${((t - cfg.min) / (anyValue - cfg.min)) * 100}%`;
    scale.appendChild(span);
  }

  container.append(input, scale);

  const label = (raw: number): string => (raw >= anyValue ? "Any" : formatBandLabel(raw));
  const emit = (): void => {
    const raw = Number(input.value);
    output.textContent = label(raw);
    input.setAttribute("aria-valuetext", label(raw));
    cfg.onChange(raw >= anyValue ? 0 : raw);
  };
  input.addEventListener("input", emit);

  return {
    update(minutes: number): void {
      const snapped = snapToStep(minutes, cfg);
      const raw = snapped <= 0 ? anyValue : snapped;
      if (Number(input.value) !== raw) input.value = String(raw);
      output.textContent = label(raw);
      input.setAttribute("aria-valuetext", label(raw));
    },
  };
}
